import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setOpenLoginAlert } from '@redux/auth/authSlice';
import {
    setGameLaunchStatus,
    setGameExitStatus,
    resetRetryCount,
    setAlert,
} from '@redux/game/gameSlice';
import {
    LIMITS,
    SERVER_ERROR_CODES,
} from '@constants';
import { api, Status } from '@api';
import { PRIVATE_ROUTE } from '@router/route.constants';
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { localStorage } from "@utils";
import { openAlert } from "../redux/app/appSlice";

const { GAME_RETRY_TIMEOUT } = LIMITS;

export const useGame = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const isLoggedIn = useSelector(state => state.auth.token);
    const {
        gameExitStatus,
        retryCount,
    } = useSelector(state => state.game);
    let launch_timer = useRef();
    let exit_timer = useRef();
    const has_withdraw_account = useSelector(state => state.app.has_withdraw_account);

    useEffect(() => {
        if (retryCount === 0) {
            dispatch(setGameExitStatus(Status.DEFAULT));
            dispatch(resetRetryCount());
            exit_timer.current && clearTimeout(exit_timer.current);
        }

        // eslint-disable-next-line
    }, [retryCount]);

    const launchGame = (game) => {
        if (!isLoggedIn) {
            dispatch(setOpenLoginAlert(true));
            return;
        }

        if (gameExitStatus === Status.LOADING || gameExitStatus === Status.ERROR) {
            showAlert(t("maintain.gameWaitingMessage"));
            return;
        }

        if (!has_withdraw_account) {
            dispatch(
                openAlert({
                    isError: true,
                    message: t("common.withdrawAccountNeededErrorMsg"),
                    successCallback: () => navigate(`/${PRIVATE_ROUTE.WITHDRAW_ACCOUNTS}`),
                })
            );
            return;
        }

        launch_timer.current && clearTimeout(launch_timer.current);
        dispatch(setGameLaunchStatus(Status.LOADING));
        dispatch(setGameExitStatus(Status.DEFAULT));
        api
            .launchGame({
                child_game_id: game.child_game_id
            })
            .then(({ url }) => {
                dispatch(setGameLaunchStatus(Status.SUCCESS));
                // go to ingame page
                navigate(`/${PRIVATE_ROUTE.IN_GAME}`, {
                    state: {
                        url,
                        game,
                    }
                });
            })
            .catch((error) => {
                if (error?.statusCode === SERVER_ERROR_CODES.TOO_MANY_REQUEST) {
                    // Retry
                    launch_timer.current = setTimeout(() => launchGame(game), GAME_RETRY_TIMEOUT * 1000);
                    return;
                }

                dispatch(setGameLaunchStatus(Status.ERROR));
                showAlert(error);
            });
    }

    const exitGame = () => {
        const now = new Date().getTime();
        const exitedTime = localStorage.getExitGame();
    
        let status = false;
    
        if (exitedTime === null) {
          status = true;
          localStorage.setExitGame(now);
        } else {
          const diff = now - exitedTime;
          if (diff > GAME_RETRY_TIMEOUT * 1000) {
            status = true;
            localStorage.setExitGame(now);
          }
        }
        if (status) {
          dispatch(setGameExitStatus(Status.LOADING));
          api
            .exitGame()
            .then(() => {
              dispatch(setGameExitStatus(Status.SUCCESS));
            })
            .catch(() => {
              dispatch(setGameExitStatus(Status.ERROR));
            });
        }
      };

    const showAlert = (description) => {
        dispatch(
            setAlert({
                open: true,
                title: t('common.notice'),
                description,
            })
        );
    };

    return {
        launchGame,
        exitGame,
    }
}